// External Advisor Recorder — public token-protected page (no login).
// The advisor (e.g. طلال الشريف) opens /external/room/:token, records a
// voice message in-browser via MediaRecorder, and uploads it.
import React, { useEffect, useRef, useState } from "react";
import { api } from "./lib/api";

// Pull the token from the URL — kept dependency-free so the public page
// works inside the existing pathname-based router in `index.js` without
// pulling in react-router-dom.
function tokenFromPath() {
  const m = (window.location.pathname || "").match(/^\/external\/room\/([^/]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

export default function ExternalRoomRecorder() {
  const token = tokenFromPath();
  const [room, setRoom] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [state, setState] = useState("idle");   // idle | recording | recorded | sending | done
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState("");

  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Fetch room info on mount.
  useEffect(() => {
    let alive = true;
    api.get(`/external/rooms/${token}`)
      .then((r) => { if (alive) { setRoom(r.data); setName(r.data?.advisor_name || ""); } })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "رابط غير صالح"); });
    return () => { alive = false; };
  }, [token]);

  const start = async () => {
    setErr("");
    setBlob(null);
    setBlobUrl("");
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick the most widely supported MIME for upload.
      const mimeCandidates = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg", "audio/mp4"];
      const mime = mimeCandidates.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const finalMime = rec.mimeType || "audio/webm";
        const b = new Blob(chunksRef.current, { type: finalMime });
        setBlob(b);
        setBlobUrl(URL.createObjectURL(b));
        // Release the mic.
        stream.getTracks().forEach((t) => t.stop());
      };
      recRef.current = rec;
      rec.start();
      setState("recording");
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) {
      setErr("تعذّر الوصول إلى الميكروفون — يرجى السماح بالوصول من المتصفح.");
    }
  };

  const stop = () => {
    try { recRef.current?.stop(); } catch (_e) { /* noop */ }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setState("recorded");
  };

  const reset = () => {
    if (blobUrl) try { URL.revokeObjectURL(blobUrl); } catch (_e) { /* noop */ }
    setBlob(null);
    setBlobUrl("");
    setElapsed(0);
    setState("idle");
  };

  const send = async () => {
    if (!blob) return;
    setState("sending");
    setErr("");
    try {
      const fd = new FormData();
      const ext = (blob.type.split("/")[1] || "webm").split(";")[0];
      fd.append("audio", blob, `voice.${ext}`);
      fd.append("advisor_name", name || (room?.advisor_name || ""));
      fd.append("note", note || "");
      await api.post(`/external/rooms/${token}/messages`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setState("done");
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر الإرسال — حاول مرة أخرى.");
      setState("recorded");
    }
  };

  if (err && !room) {
    return (
      <div style={wrap}>
        <div style={card}>
          <h2 style={{ margin: 0, color: "#B91C1C" }}>⚠️ خطأ</h2>
          <p style={{ color: "#6B7280" }}>{err}</p>
        </div>
      </div>
    );
  }
  if (!room) return <div style={wrap}><div style={card}>⏳ جاري التحميل...</div></div>;

  return (
    <div style={wrap} data-testid="external-room-page">
      <div style={card}>
        <div style={{ textAlign: "center", marginBottom: 18 }}>
          <div style={{ fontSize: 38 }}>🎙️</div>
          <h1 style={{ margin: "6px 0 4px", color: "#4C1D95", fontSize: 22 }}>{room.name}</h1>
          {room.description && <div style={{ color: "#6B7280", fontSize: 13 }}>{room.description}</div>}
        </div>

        {state === "done" ? (
          <div style={{ textAlign: "center", padding: "20px 12px" }}>
            <div style={{ fontSize: 52 }}>✅</div>
            <h3 style={{ margin: "10px 0", color: "#16A34A" }}>وصلت الرسالة بنجاح</h3>
            <p style={{ color: "#6B7280", fontSize: 13 }}>سيتم تفريغها بالعربية تلقائياً وتظهر للفريق خلال ثوانٍ.</p>
            <button onClick={reset} data-testid="record-new"
              style={btnSecondary}>🎙️ تسجيل رسالة أخرى</button>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>اسم المُرسِل</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="مثل: طلال الشريف"
                data-testid="advisor-name-input" style={inp} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>ملاحظة قصيرة (اختياري)</label>
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="عنوان الموضوع"
                data-testid="advisor-note-input" style={inp} />
            </div>

            <div style={{
              padding: 24, textAlign: "center", borderRadius: 12,
              background: state === "recording" ? "rgba(220,38,38,.06)" : "rgba(124,58,237,.05)",
              border: `2px ${state === "recording" ? "solid #DC2626" : "dashed #C4B5FD"}`,
            }}>
              {state === "idle" && (
                <button onClick={start} data-testid="record-start-btn"
                  style={btnHuge("#7C3AED")}>🎙️ ابدأ التسجيل</button>
              )}
              {state === "recording" && (
                <>
                  <div style={{ fontSize: 36, fontWeight: 800, color: "#DC2626" }}>
                    ⏺ {fmt(elapsed)}
                  </div>
                  <button onClick={stop} data-testid="record-stop-btn"
                    style={{ ...btnHuge("#DC2626"), marginTop: 12 }}>⏹ إيقاف</button>
                </>
              )}
              {state === "recorded" && (
                <>
                  <audio src={blobUrl} controls data-testid="recorded-audio"
                    style={{ width: "100%", marginBottom: 12 }} />
                  <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
                    <button onClick={reset} style={btnSecondary} data-testid="record-discard">🗑️ إعادة</button>
                    <button onClick={send} data-testid="record-send-btn"
                      style={btnHuge("#16A34A")}>📤 إرسال</button>
                  </div>
                </>
              )}
              {state === "sending" && (
                <div style={{ color: "#4C1D95", fontSize: 16, fontWeight: 700 }}>⏳ جاري الإرسال...</div>
              )}
            </div>

            {err && (
              <div style={{ marginTop: 12, padding: 10, background: "rgba(220,38,38,.08)",
                color: "#B91C1C", borderRadius: 6, fontSize: 13, textAlign: "center" }}>
                {err}
              </div>
            )}
          </>
        )}

        <div style={{ textAlign: "center", marginTop: 22, color: "#9CA3AF", fontSize: 11 }}>
          ثواني · Thawany — قاعة اجتماعات
        </div>
      </div>
    </div>
  );
}

function fmt(s) {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const ss = (s % 60).toString().padStart(2, "0");
  return `${m}:${ss}`;
}

const wrap = {
  minHeight: "100vh", padding: 16, display: "flex", alignItems: "center", justifyContent: "center",
  background: "linear-gradient(180deg, #FAF5FF, #F3F4F6)",
};
const card = {
  width: "100%", maxWidth: 480, background: "#fff", padding: 24,
  borderRadius: 14, boxShadow: "0 12px 32px rgba(0,0,0,.10)",
};
const lbl = { fontSize: 12.5, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 };
const inp = { width: "100%", padding: "9px 11px", borderRadius: 6, border: "1px solid #ddd", fontSize: 14, fontFamily: "inherit" };
const btnHuge = (bg) => ({
  padding: "14px 26px", background: bg, color: "#fff", border: "none",
  borderRadius: 8, fontSize: 16, fontWeight: 800, cursor: "pointer",
});
const btnSecondary = {
  padding: "10px 18px", background: "#fff", color: "#4C1D95",
  border: "1px solid #4C1D95", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer",
};
